/**
 * Page de liste des To Dos avec tri et filtres.
 * Supporte la vue equipe pour les squad leads.
 */

import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getTodos, deleteTodo, updateTodo } from "../api/todoApi";
import { getTeamTodos } from "../api/teamApi";
import { useTeamView } from "../contexts/TeamViewContext";
import SortableTable from "../components/SortableTable";
import StatusBadge from "../components/StatusBadge";
import ConfirmDialog from "../components/ConfirmDialog";
import { formatDate, isOverdue } from "../utils/dateUtils";
import { STAKE_LABELS, URGENCY_LABELS } from "../utils/constants";
import "./PageStyles.css";

export default function TodoListPage() {
  const [todos, setTodos] = useState([]);
  const [sortBy, setSortBy] = useState("due_date");
  const [deleteId, setDeleteId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const { teamView } = useTeamView();

  useEffect(() => {
    loadTodos();
  }, [sortBy, teamView]);

  async function loadTodos() {
    const data = teamView
      ? await getTeamTodos(sortBy, "in_progress")
      : await getTodos(sortBy, "in_progress");
    setTodos(data);
  }

  async function handleDelete() {
    await deleteTodo(deleteId);
    setDeleteId(null);
    loadTodos();
  }

  function startEdit(t) {
    setEditingId(t.id);
    setEditText(t.description || "");
    setTimeout(() => inputRef.current?.focus(), 0);
  }

  async function saveEdit(id) {
    if (editingId !== id) return;
    const todo = todos.find((t) => t.id === id);
    if (todo && editText !== (todo.description || "")) {
      await updateTodo(id, { description: editText });
      setTodos((prev) =>
        prev.map((t) => (t.id === id ? { ...t, description: editText } : t))
      );
    }
    setEditingId(null);
  }

  async function markDone(t) {
    await updateTodo(t.id, { status: "done" });
    setTodos((prev) => prev.filter((item) => item.id !== t.id));
  }

  const columns = [
    { key: "contact_name", label: "Contact", sortable: true },
    { key: "company", label: "Société", sortable: true },
    { key: "owner_name", label: "Créateur", sortable: true },
    { key: "description", label: "Description" },
    { key: "responsable_name", label: "Responsable", sortable: true },
    { key: "due_date", label: "Échéance", sortable: true },
    { key: "stake_level", label: "Enjeu", sortable: true },
    { key: "urgency_level", label: "Urgence", sortable: true },
    { key: "tags", label: "Tags" },
    { key: "operations", label: "" },
  ];

  function renderRow(t, _i, hiddenCols) {
    const h = hiddenCols || {};
    const overdue = t.status === "in_progress" && isOverdue(t.due_date);
    // Droit de gestion calculé côté backend (propriétaire, contact public, ou
    // squad/practice lead du propriétaire du contact).
    const canEdit = Boolean(t.can_edit);
    const isEditing = editingId === t.id;
    return (
      <tr key={t.id}>
        {!h.contact_name && (
          <td>
            <a href={`/contacts/${t.contact_id}`} onClick={(e) => { e.preventDefault(); navigate(`/contacts/${t.contact_id}`); }}>
              {t.contact_name || "—"}
            </a>
          </td>
        )}
        {!h.company && <td>{t.contact_company || "—"}</td>}
        {!h.owner_name && <td>{t.owner_name || "—"}</td>}
        {!h.description && (
          <td
            className={canEdit && !isEditing ? "cell-editable" : ""}
            onClick={canEdit && !isEditing ? () => startEdit(t) : undefined}
            title={isEditing ? undefined : t.description}
          >
            {isEditing ? (
              <input
                ref={inputRef}
                className="cell-edit-input"
                type="text"
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                onBlur={() => saveEdit(t.id)}
                onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); if (e.key === "Escape") { setEditingId(null); } }}
              />
            ) : (
              t.description || ""
            )}
          </td>
        )}
        {!h.responsable_name && <td>{t.responsable_name || "—"}</td>}
        {!h.due_date && (
          <td className={overdue ? "cell-overdue" : ""}>
            {formatDate(t.due_date)}
          </td>
        )}
        {!h.stake_level && (
          <td>
            {t.stake_level && (
              <StatusBadge value={t.stake_level} label={STAKE_LABELS[t.stake_level]} />
            )}
          </td>
        )}
        {!h.urgency_level && (
          <td>
            {t.urgency_level && (
              <StatusBadge value={t.urgency_level} label={URGENCY_LABELS[t.urgency_level]} />
            )}
          </td>
        )}
        {!h.tags && (
          <td>
            <div className="tag-list">
              {t.tags?.map((tag) => (
                <span key={tag.id} className="tag-inline">{tag.label}</span>
              ))}
            </div>
          </td>
        )}
        {!h.operations && (
          <td className="row-actions">
            {canEdit && (
              <div className="row-actions-inner">
                <button className="btn btn-small" onClick={() => markDone(t)}>
                  Réalisé
                </button>
                <button className="btn btn-small" onClick={() => navigate(`/todos/${t.id}/edit`)}>
                  Modifier
                </button>
                <button className="btn btn-small btn-danger" onClick={() => setDeleteId(t.id)}>
                  Supprimer
                </button>
              </div>
            )}
          </td>
        )}
      </tr>
    );
  }

  return (
    <div className="page">
      <div className="page-header">
        <h2>{teamView ? "Les To Dos de mon équipe" : "Mes To Dos"}</h2>
        {!teamView && (
          <Link to="/todos/new" className="btn btn-primary">
            Nouveau To Do
          </Link>
        )}
      </div>
      <SortableTable
        columns={columns}
        rows={todos}
        renderRow={renderRow}
        sortBy={sortBy}
        onSort={setSortBy}
        emptyMessage="Aucun To Do pour le moment."
        storageKey="todos_table"
      />
      {deleteId && (
        <ConfirmDialog
          message="Supprimer ce To Do ?"
          onConfirm={handleDelete}
          onCancel={() => setDeleteId(null)}
        />
      )}
    </div>
  );
}
